import React, { useState, useEffect } from 'react';
import {
    Box,
    Chip,
    Tabs,
    Tab,
} from '@mui/material';
import { AutoAwesome, Description } from '@mui/icons-material';
import { PromptVersion } from './types';
import { VersionSelector } from './components/VersionSelector';
import { RefineTab } from './components/RefineTab';
import { PreviewTab } from './components/PreviewTab';

interface PromptEditorProps {
    jobId: string;
    expertId: string;
    activePrompt?: PromptVersion;
    versions: PromptVersion[];
    selectedVersionId: string;
    onVersionSelect: (versionId: string) => void;
    onSetAsActive: (versionId: string) => void;
    onRegeneratePrompt: (experimentName: string, modificationRequest: string, onSuccess?: () => void) => void;
    isRegenerating?: boolean;
    onNavigateToPreview?: () => void;
    draftPrompt: string;
    onDraftPromptChange: (prompt: string) => void;
    onDeleteVersion?: (versionId: string) => void;
    onNavigateToTest?: () => void;
    evaluationExperiment?: boolean | null;
}

type SubTab = 'refine' | 'preview';

export const PromptEditor: React.FC<PromptEditorProps> = ({
    versions,
    selectedVersionId,
    onVersionSelect,
    onSetAsActive,
    onRegeneratePrompt,
    isRegenerating = false,
    draftPrompt,
    onDraftPromptChange,
    onDeleteVersion,
    onNavigateToTest,
    evaluationExperiment,
}) => {
    const [subTab, setSubTab] = useState<SubTab>('refine');
    const [experimentName, setExperimentName] = useState('');
    const [modificationRequest, setModificationRequest] = useState('');

    const selectedVersion = versions.find(v => v.id === selectedVersionId);
    const displayPrompt = draftPrompt || selectedVersion?.prompt?.userTemplate || '';

    // Populate fields from draft when draft is selected
    useEffect(() => {
        if (selectedVersion?.status === 'DRAFT' || evaluationExperiment) {
            if (selectedVersion?.experimentName) {
                setExperimentName(selectedVersion.experimentName);
            }
            if (selectedVersion?.notes) {
                setModificationRequest(selectedVersion.notes);
            }
        } else {
            // Clear fields when switching to non-draft version
            setExperimentName('');
            setModificationRequest('');
        }
    }, [selectedVersionId, selectedVersion?.status, selectedVersion?.experimentName, selectedVersion?.notes, evaluationExperiment]);

    const handleSuggestionClick = (suggestion: string) => {
        setModificationRequest(prev => {
            if (!prev.trim()) {
                return suggestion;
            }
            return `${prev}, ${suggestion}`;
        });
    };

    const handleRegeneratePrompt = () => {
        onRegeneratePrompt(experimentName, modificationRequest, () => {
            evaluationExperiment ? onNavigateToTest?.() : setSubTab('preview');
        });
    };

    const handleSubTabChange = (event: React.SyntheticEvent, newValue: SubTab) => {
        setSubTab(newValue);
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Sub-tabs: Refine and Preview */}
            {!evaluationExperiment && (
                <Box sx={{ mb: 3 }}>
                    <Tabs
                        value={subTab}
                        onChange={handleSubTabChange}
                        sx={{
                            minHeight: 0,
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                minHeight: 0,
                                py: 1.5,
                            },
                        }}
                    >
                        <Tab
                            icon={<AutoAwesome sx={{ fontSize: 18 }} />}
                            iconPosition="start"
                            label="Refine"
                            value="refine"
                        />
                        <Tab
                            icon={<Description sx={{ fontSize: 18 }} />}
                            iconPosition="start"
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    Preview
                                    {selectedVersion?.status === 'DRAFT' && (
                                        <Chip
                                            label="1"
                                            size="small"
                                            sx={{
                                                height: 18,
                                                fontSize: '0.7rem',
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                            }}
                                        />
                                    )}
                                </Box>
                            }
                            value="preview"
                        />
                    </Tabs>
                </Box>
            )}

            {/* Refine Sub-tab Content */}
            {subTab === 'refine' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, overflow: 'auto', pr: 1 }}>
                    <VersionSelector
                        versions={versions}
                        selectedVersionId={selectedVersionId}
                        onVersionSelect={onVersionSelect}
                        onSetAsActive={onSetAsActive}
                        onDeleteVersion={onDeleteVersion}
                    />

                    <RefineTab
                        selectedVersion={selectedVersion}
                        displayPrompt={displayPrompt}
                        experimentName={experimentName}
                        setExperimentName={setExperimentName}
                        modificationRequest={modificationRequest}
                        setModificationRequest={setModificationRequest}
                        isRegenerating={isRegenerating}
                        handleRegeneratePrompt={handleRegeneratePrompt}
                        handleSuggestionClick={handleSuggestionClick}
                        onNext={() => evaluationExperiment === true ? onNavigateToTest?.() : setSubTab('preview')}
                        evaluationExperiment={evaluationExperiment}
                    />
                </Box>
            )}

            {/* Preview Sub-tab Content */}
            {subTab === 'preview' && (
                <PreviewTab
                    selectedVersion={selectedVersion}
                    displayPrompt={displayPrompt}
                    onBack={() => setSubTab('refine')}
                    onTest={onNavigateToTest}
                />
            )}
        </Box>
    );
};
